import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";
import { app, auth } from "../firebase";
import { brand } from "../theme";
import BottomNav from "./BottomNav";
import useMembership from "./useMembership";
import { PageShell, PageHeader, PageContent, Section } from "./Page";

export const MEMBERSHIP_PRICE = "7,90 USD";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const PAYPAL_PLAN_ID = process.env.REACT_APP_PAYPAL_PLAN_ID;

const functions = getFunctions(app, "us-central1");
const activateMembership = httpsCallable(functions, "activateMembership");
const cancelMembership = httpsCallable(functions, "cancelMembership");

const benefits = [
  "Publicaciones de vehículos ilimitadas",
  "Editá precio, fotos y datos cuando quieras",
  "Chateá directo con los compradores",
  "Cancelás cuando quieras desde esta sección",
];

const formatDate = (date) =>
  date ? date.toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : "";

const Membership = () => {
  const [user] = useAuthState(auth);
  const { loading, membership, isActive, isComplimentary, validUntil } = useMembership();
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const isRenewing = !isComplimentary && isActive && membership?.status === "ACTIVE";
  const isPending = !isActive && membership?.status === "APPROVED";

  const handleApprove = async (data) => {
    setProcessing(true);
    setStatus(null);
    try {
      await activateMembership({ subscriptionId: data.subscriptionID });
      setStatus({ severity: "success", message: "¡Listo! Tu membresía está activa. Ya podés publicar." });
    } catch {
      setStatus({
        severity: "warning",
        message: "Recibimos tu suscripción y la estamos confirmando con PayPal. Puede demorar unos minutos.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    setConfirmCancel(false);
    setProcessing(true);
    setStatus(null);
    try {
      await cancelMembership();
      setStatus({ severity: "info", message: "Cancelaste la renovación automática. No se te va a volver a cobrar." });
    } catch {
      setStatus({ severity: "error", message: "No pudimos cancelar la suscripción. Probá de nuevo en unos minutos." });
    } finally {
      setProcessing(false);
    }
  };

  const renderPayment = () => {
    if (!PAYPAL_CLIENT_ID || !PAYPAL_PLAN_ID) {
      return <Alert severity="warning">Los pagos todavía no están configurados.</Alert>;
    }
    if (processing) {
      return (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
          <CircularProgress color="secondary" />
          <Typography variant="body2" color="text.secondary">
            Confirmando con PayPal...
          </Typography>
        </Stack>
      );
    }
    return (
      <PayPalScriptProvider
        options={{ clientId: PAYPAL_CLIENT_ID, vault: true, intent: "subscription", currency: "USD", locale: "es_AR" }}
      >
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", color: "gold", label: "subscribe" }}
          createSubscription={(_, actions) =>
            // custom_id vincula la suscripción con esta cuenta; la Cloud Function lo verifica
            actions.subscription.create({ plan_id: PAYPAL_PLAN_ID, custom_id: user?.uid })
          }
          onApprove={handleApprove}
          onError={() =>
            setStatus({ severity: "error", message: "PayPal no pudo procesar el pago. Probá de nuevo." })
          }
        />
      </PayPalScriptProvider>
    );
  };

  return (
    <PageShell>
      <PageHeader title="Membresía" subtitle="Publicá tus vehículos en Caro" />
      <PageContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Stack spacing={3}>
            {status && <Alert severity={status.severity}>{status.message}</Alert>}

            <Grid container spacing={3}>
              {/* Plan */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: "100%",
                    boxSizing: "border-box",
                    borderRadius: 4,
                    color: brand.cream,
                    background: `radial-gradient(circle at 100% 0%, ${brand.coral}40 0%, transparent 50%), linear-gradient(150deg, ${brand.ink} 0%, ${brand.inkDark} 100%)`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <WorkspacePremiumIcon sx={{ color: brand.coral, fontSize: 32 }} />
                    <Typography variant="h6">Membresía Caro</Typography>
                    {isActive && <Chip label="Activa" color="secondary" size="small" />}
                  </Stack>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 3 }}>
                    <Typography sx={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
                      {MEMBERSHIP_PRICE}
                    </Typography>
                    <Typography sx={{ opacity: 0.7 }}>/ mes</Typography>
                  </Stack>
                  <Stack spacing={1.5} sx={{ mt: 3 }}>
                    {benefits.map((benefit) => (
                      <Stack key={benefit} direction="row" spacing={1.5} alignItems="center">
                        <CheckCircleIcon sx={{ color: brand.coral, fontSize: 20 }} />
                        <Typography sx={{ opacity: 0.9 }}>{benefit}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>

              {/* Estado o pago */}
              <Grid item xs={12} md={6}>
                {isActive ? (
                  <Section title="Tu membresía" sx={{ height: "100%", boxSizing: "border-box" }}>
                    <Stack spacing={2}>
                      <Typography color="text.secondary">
                        {isComplimentary
                          ? "Tenés una membresía de cortesía sin vencimiento. No se te cobra nada."
                          : isRenewing
                          ? `Se renueva automáticamente el ${formatDate(membership?.nextBillingTime?.toDate?.())}.`
                          : `Cancelaste la renovación. Podés publicar hasta el ${formatDate(validUntil)}.`}
                      </Typography>
                      <Button component={Link} to="/publish" variant="contained" color="secondary" size="large">
                        Publicar un vehículo
                      </Button>
                      {isRenewing && (
                        <Button color="inherit" onClick={() => setConfirmCancel(true)} disabled={processing}>
                          Cancelar suscripción
                        </Button>
                      )}
                    </Stack>
                  </Section>
                ) : (
                  <Section
                    title={isPending ? "Confirmando tu pago" : "Suscribite"}
                    description={
                      isPending
                        ? "PayPal está procesando tu suscripción. Se activa automáticamente en unos minutos."
                        : "Pagás con tu cuenta de PayPal o con tarjeta. La renovación es mensual y podés cancelarla cuando quieras."
                    }
                    sx={{ height: "100%", boxSizing: "border-box" }}
                  >
                    {!isPending && renderPayment()}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, color: "text.secondary" }}>
                      <LockOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        El pago se procesa en PayPal. Caro no guarda los datos de tu tarjeta.
                      </Typography>
                    </Stack>
                  </Section>
                )}
              </Grid>
            </Grid>
          </Stack>
        )}
      </PageContent>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>¿Cancelar la suscripción?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            No se te va a volver a cobrar. Vas a poder seguir publicando hasta el {formatDate(validUntil)}.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmCancel(false)}>
            Volver
          </Button>
          <Button color="error" variant="contained" onClick={handleCancel}>
            Cancelar suscripción
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </PageShell>
  );
};

export default Membership;
